import React, { useEffect, useState } from 'react';
import { api } from '@/api/client';
import type { BuildInfo, RuntimeBuild } from '@/types';

type BuildLike = BuildInfo | RuntimeBuild | null | undefined;

const UI_COMMIT = (process.env.REACT_APP_GIT_COMMIT || 'unknown').slice(0, 7);
const UI_IMAGE_TAG = process.env.REACT_APP_IMAGE_TAG || process.env.REACT_APP_BUILD_NUMBER || 'local';
const UI_ENV = process.env.REACT_APP_ENV || process.env.REACT_APP_BUILD_NUMBER || 'local';
const UI_BUILD_TIME = process.env.REACT_APP_BUILD_TIME || '';

function commitShort(build: BuildLike): string {
  if (!build) return UI_COMMIT !== 'unknown' ? UI_COMMIT : 'unknown';
  const short =
    ('git_commit_short' in build && build.git_commit_short) ||
    ('commit_short' in build && build.commit_short) ||
    ('git_commit' in build && build.git_commit) ||
    ('commit' in build && build.commit);
  if (short && short !== 'unknown') return short.slice(0, 7);
  return UI_COMMIT !== 'unknown' ? UI_COMMIT : 'unknown';
}

function branchName(build: BuildLike): string {
  if (!build) return process.env.REACT_APP_GIT_BRANCH || 'unknown';
  return (
    ('git_branch' in build && build.git_branch) ||
    ('branch' in build && build.branch) ||
    process.env.REACT_APP_GIT_BRANCH ||
    'unknown'
  );
}

function imageTag(build: BuildLike): string {
  if (!build) return UI_IMAGE_TAG;
  return (
    ('image_tag' in build && build.image_tag) ||
    ('version' in build && build.version) ||
    UI_IMAGE_TAG
  );
}

function environmentName(build: BuildLike): string {
  if (!build) return UI_ENV;
  return (
    ('environment' in build && build.environment) ||
    ('env' in build && build.env) ||
    ('app_env' in build && build.app_env) ||
    ('build_number' in build && build.build_number) ||
    UI_ENV
  );
}

function buildDate(build: BuildLike): string | null {
  if (!build) return UI_BUILD_TIME || null;
  const raw =
    ('build_date' in build && build.build_date) ||
    ('build_time' in build && build.build_time) ||
    UI_BUILD_TIME;
  return raw && String(raw).trim() ? String(raw).trim() : null;
}

function formatBuildDateShort(build: BuildLike): string {
  const raw = buildDate(build);
  if (!raw) return 'unknown';
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return raw.length >= 10 ? raw.slice(0, 10) : raw;
}

function fullCommit(build: BuildLike): string {
  if (!build) return process.env.REACT_APP_GIT_COMMIT || 'unknown';
  return (
    ('git_commit' in build && build.git_commit) ||
    ('commit' in build && build.commit) ||
    process.env.REACT_APP_GIT_COMMIT ||
    'unknown'
  );
}

export function formatBuildLabel(build: BuildLike): string {
  const commit = commitShort(build);
  const date = formatBuildDateShort(build);
  const tag = imageTag(build);
  return `${commit} · ${date} · ${tag}`;
}

export function formatBuildFooterLabel(build: BuildLike): string {
  const base = formatBuildLabel(build);
  const mode =
    build && 'deployment_mode' in build && build.deployment_mode
      ? String(build.deployment_mode)
      : null;
  return mode ? `${base} · ${mode}` : base;
}

export function buildMetadataTooltip(build: BuildLike): string {
  return [
    `Commit: ${fullCommit(build)}`,
    `Branch: ${branchName(build)}`,
    `Image tag: ${imageTag(build)}`,
    `Environment: ${environmentName(build)}`,
    buildDate(build) ? `Built: ${buildDate(build)}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

type Props = {
  build?: BuildLike;
  className?: string;
  fetchIfMissing?: boolean;
  formatLabel?: (build: BuildLike) => string;
};

export default function BuildMetadataBadge({
  build: buildProp,
  className,
  fetchIfMissing = true,
  formatLabel = formatBuildLabel,
}: Props) {
  const [fetched, setFetched] = useState<BuildInfo | null>(null);

  useEffect(() => {
    if (buildProp || !fetchIfMissing) return;
    let cancelled = false;
    api.dashboard
      .buildInfo()
      .then((data) => {
        if (!cancelled) setFetched(data);
      })
      .catch(() => {
        /* fallback to baked-in env vars */
      });
    return () => {
      cancelled = true;
    };
  }, [buildProp, fetchIfMissing]);

  const build = buildProp ?? fetched;
  const label = formatLabel(build);
  const title = buildMetadataTooltip(build);

  return (
    <span className={className ?? 'build-metadata-badge'} title={title}>
      {label}
    </span>
  );
}
